import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";

let connection = null;
let notificationCallbacks = [];
let unreadCountCallbacks = [];
let connectionStartPromise = null;
let retryCount = 0;
const MAX_RETRIES = 3;

export const startSignalRConnection = async (token) => {
  if (!token) {
    throw new Error("Authentication token required");
  }

  // If we're already starting a connection, return that promise
  if (connectionStartPromise) {
    return connectionStartPromise;
  }

  // If we have a connected connection, return it
  if (connection && connection.state === "Connected") {
    return connection;
  }

  // Clean up any existing connection
  if (connection) {
    try {
      await stopSignalRConnection();
    } catch (err) {
      console.warn("Error while cleaning up previous connection:", err);
    }
  }

  try {
    connectionStartPromise = (async () => {
      connection = new HubConnectionBuilder()
        .withUrl(`${process.env.REACT_APP_LIVE_DOMAIN_URL}notificationHub`, {
          accessTokenFactory: () => token,
          skipNegotiation: true,
          transport: 1, // WebSockets only
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (retryContext.elapsedMilliseconds > 60000) {
              return null; // Stop trying after 1 minute
            }
            return Math.min(retryContext.elapsedMilliseconds * 2, 10000);
          },
        })
        .configureLogging(LogLevel.Warning)
        .build();

      // Setup handlers
      connection.on("ReceiveNotification", (notification) => {
        notificationCallbacks.forEach((callback) => callback(notification));
      });

      connection.on("UpdateUnreadCount", (count) => {
        unreadCountCallbacks.forEach((callback) => callback(count));
      });

      connection.onclose((error) => {
        console.log("SignalR connection closed", error);
        if (error && retryCount < MAX_RETRIES) {
          retryCount++;
          console.log(`Attempting reconnect (${retryCount}/${MAX_RETRIES})`);
          setTimeout(() => startSignalRConnection(token), 2000);
        }
      });

      await connection.start();
      console.log("SignalR Connected successfully");
      retryCount = 0;
      return connection;
    })();

    return await connectionStartPromise;
  } catch (err) {
    console.error("SignalR Connection Error:", err);
    connectionStartPromise = null;
    await stopSignalRConnection();
    throw err;
  } finally {
    connectionStartPromise = null;
  }
};

export const registerNotificationCallback = (callback) => {
  notificationCallbacks.push(callback);
  return () => {
    notificationCallbacks = notificationCallbacks.filter(
      (cb) => cb !== callback
    );
  };
};

export const registerUnreadCountCallback = (callback) => {
  unreadCountCallbacks.push(callback);
  return () => {
    unreadCountCallbacks = unreadCountCallbacks.filter((cb) => cb !== callback);
  };
};

export const stopSignalRConnection = async () => {
  if (!connection) return;

  try {
    // Clear any pending connection attempts
    connectionStartPromise = null;

    if (connection.state === "Connected") {
      await connection.stop().catch((err) => {
        console.warn("Error while stopping connection:", err);
      });
    }
  } catch (err) {
    console.error("Error stopping SignalR connection:", err);
  } finally {
    connection = null;
    notificationCallbacks = [];
    unreadCountCallbacks = [];
  }
};

export const getConnectionState = () => {
  return connection ? connection.state : "Disconnected";
};

export const isConnectionStarting = () => {
  return !!connectionStartPromise;
};
